import {useCommentEditsContext} from '@github-ui/commenting/CommentEditsContext'
import {useBlocker} from '@github-ui/react-router'
import {SOFT_NAV_STATE} from '@github-ui/soft-nav/states'
import {useEffect} from 'react'

const CONFIRM_LEAVE = 'Are you sure you want to leave? You have unsaved changes.'
export const ISSUE_VIEWER_NAVIGATION_BYPASS_STATE_KEY = 'bypassIssueViewerNavigationBlocker'

export function shouldBlockIssueViewerNavigation(
  isCommentEditActive: boolean,
  currentPath: string,
  navigationState: unknown,
): boolean {
  if (!isCommentEditActive || !navigationState || typeof navigationState !== 'object') return isCommentEditActive
  return Reflect.get(navigationState, ISSUE_VIEWER_NAVIGATION_BYPASS_STATE_KEY) !== currentPath
}

function guardIssueViewerNavigation(
  isCommentEditActive: boolean,
  cancelAllCommentEdits: () => void,
  confirmLeave: () => boolean,
): boolean {
  if (!isCommentEditActive) return true
  if (!confirmLeave()) return false
  cancelAllCommentEdits()
  return true
}

export function useIssueViewerNavigationBlocker() {
  const {isCommentEditActive, cancelAllCommentEdits} = useCommentEditsContext()
  const blocker = useBlocker(({currentLocation, nextLocation}) =>
    shouldBlockIssueViewerNavigation(
      isCommentEditActive(),
      currentLocation.pathname,
      Reflect.get(nextLocation, 'state'),
    ),
  )

  useEffect(() => {
    if (blocker.state !== 'blocked') return

    const timeoutId = setTimeout(() => {
      if (!guardIssueViewerNavigation(isCommentEditActive(), cancelAllCommentEdits, () => confirm(CONFIRM_LEAVE))) {
        blocker.reset?.()
        // React Router already started the navigation, so the progress bar needs ending by hand.
        document.dispatchEvent(new Event(SOFT_NAV_STATE.END))
        document.dispatchEvent(new Event(SOFT_NAV_STATE.PROGRESS_BAR.END))
        return
      }
      blocker.proceed?.()
    }, 0)
    return () => clearTimeout(timeoutId)
  }, [blocker, cancelAllCommentEdits, isCommentEditActive])
}
